import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { constants as imagesConstants } from '../../../../../../redux/images';
import { constants as workflowConstants } from '../../../../../../redux/workflows';

import { Card } from '../../../../../../components';

import ImageQualityValidationSettings from '../ImageQualityValidationSettings/ImageQualityValidationSettings';
import SmartScanCommentSettings from '../SmartScanCommentSettings/SmartScanCommentSettings';
import VinInstructionSettings from './VinInstructionSettings';

import './sass/vin-image-type-modal.scss';

const { FIRST_VIN, INSPECTION_STICKER, SECOND_VIN } = imagesConstants.IMAGE_SUB_TYPES;
const { TRAILER, CAR, TRUCK } = workflowConstants.VEHICLE_TYPES;

const SELECTION = {
  VIN: 0,
  INSPECTION_STICKER: 1
};

const VinImageTypeModal = ({ values, setFieldValue, imageTypeErrors, imageSubTypes, vehicleType }) => {
  const noVinError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.NO_VIN);

  const vinMismatchError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.VIN_MISMATCH);

  const invalidVinError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.INVALID_VIN);

  const blurryImageError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.BLURRY_IMAGE);

  const [selection, setSelection] = useState(false);
  useEffect(() => {
    if (imageSubTypes.some((subType) => subType.imageSubType === FIRST_VIN || subType.imageSubType === SECOND_VIN)) {
      setSelection(SELECTION.VIN);
    } else if (imageSubTypes.some((subType) => subType.imageSubType === INSPECTION_STICKER)) {
      setSelection(SELECTION.INSPECTION_STICKER);
    }
  }, [imageSubTypes]);

  const onChangeHandler = (imageSubTypes) => {
    setFieldValue('imageSubTypes', imageSubTypes);
  };

  return (
    <div className="vin-image-type-modal">
      <div className="row mb-3">
        <div className="col-sm-12">
          <Card>
            <Card.Header>
              Requested images
            </Card.Header>
            <Card.Body>
              <div className="row mt-2">
                <div className="col-sm-12 sub-section">
                  <span className="sub-section-header">IMAGE TYPE</span>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-sm-4">
                  <label htmlFor="vin">
                    <input
                      type="radio"
                      name="vinSubTypeSelection"
                      checked={selection === SELECTION.VIN}
                      id="vin"
                      onChange={() => {
                        // Initialize with FIRST_VIN when VIN is selected
                        onChangeHandler([{ imageSubType: FIRST_VIN, instructions: [] }]);
                        setSelection(SELECTION.VIN);
                      }}
                    />
                    &nbsp; VIN capture
                  </label>
                </div>
                {
                  vehicleType !== TRAILER && (
                    <div className="col-sm-4">
                      <label htmlFor="inspectionSticker">
                        <input
                          type="radio"
                          name="vinSubTypeSelection"
                          checked={selection === SELECTION.INSPECTION_STICKER}
                          id="inspectionSticker"
                          onChange={() => {
                            onChangeHandler([{
                              imageSubType: INSPECTION_STICKER,
                              instructions: []
                            }]);
                            setSelection(SELECTION.INSPECTION_STICKER);
                          }}
                        />
                        &nbsp; Inspection sticker
                      </label>
                    </div>
                  )
                }
              </div>
              {selection === SELECTION.VIN && imageSubTypes.length > 0 && (vehicleType === CAR || vehicleType === TRUCK) && (
                <>
                  <div className="row mt-2">
                    <div className="col-sm-12 sub-section">
                      <span className="sub-section-header">INSTRUCTION SETTINGS</span>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-sm-12">
                      <VinInstructionSettings
                        imageSubTypes={imageSubTypes}
                        setFieldValue={setFieldValue}
                        vehicleType={vehicleType}
                      />
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-sm-12">
          <Card>
            <Card.Header>
              AI validations
            </Card.Header>
            <Card.Body>
              <div className="row mt-2">
                <div className="col-sm-12 sub-section">
                  <span className="sub-section-header">IMAGE CONTENT</span>
                </div>
              </div>
              <div className="row mt-2">
                {
                  noVinError && (
                    <div className="col-sm-4">
                      <ImageQualityValidationSettings
                        error={imagesConstants.IMAGE_VERIFICATION_ERROR.NO_VIN}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  )
                }
                {
                  vinMismatchError && (
                    <div className="col-sm-4">
                      <ImageQualityValidationSettings
                        error={imagesConstants.IMAGE_VERIFICATION_ERROR.VIN_MISMATCH}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  )
                }
                {
                  invalidVinError && (
                    <div className="col-sm-4">
                      <ImageQualityValidationSettings
                        error={imagesConstants.IMAGE_VERIFICATION_ERROR.INVALID_VIN}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  )
                }
              </div>
              <div className="row">
                {
                  blurryImageError && (
                    <div className="col-sm-4">
                      <ImageQualityValidationSettings
                        error={imagesConstants.IMAGE_VERIFICATION_ERROR.BLURRY_IMAGE}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  )
                }
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <Card>
            <Card.Header>
              Other settings
            </Card.Header>
            <Card.Body>
              <div className="row mt-2">
                <div className="col-sm-12 sub-section">
                  <span className="sub-section-header">COMMENTS</span>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-sm-12 smart-scan-comment-settings">
                  <SmartScanCommentSettings values={values} setFieldValue={setFieldValue} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

VinImageTypeModal.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  imageTypeErrors: PropTypes.array.isRequired,
  imageSubTypes: PropTypes.array,
  vehicleType: PropTypes.number.isRequired
};

export default VinImageTypeModal;
